import { pdf } from "@react-pdf/renderer";
import { VetDocumentTemplate } from "@/fluxvet/components/documents/VetDocumentTemplate";
import { generateQRCodeBase64 } from "@/fluxvet/utils/qrCodeGenerator";
import * as uuid from "uuid";
import base64 from "base-64";
import React from "react";
import { endpoints } from "@/configs/Settings";

// Importa o componente como tipo any para evitar problemas de tipo com @react-pdf/renderer
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createPdfElement = (component: any) => component;

// =============================================================================
// INTERFACES
// =============================================================================

/**
 * Resultado da geração de documento
 */
export interface DocumentGenerationResult {
  base64: string; // PDF em base64
  uuid: string; // UUID único do documento

  idValue: string;
}

interface Treatment {
  id: string;
  name: string;
  dosage: string;
  description: string;
}

interface Exam {
  id: string;
  description: string;
  notes: string;
}

interface Report {
  id: string;
  content: string;
}

interface PetInfo {
  id: string;
  name: string;
  species: string;
  breed: string;
  age?: string;
}

interface TutorInfo {
  name: string;
  document?: string;
  phone?: string;
}

interface GenerateDocumentParams {
  documentType: "treatment" | "exam" | "report" | "combined";
  treatments?: Treatment[];
  exams?: Exam[];
  reports?: Report[];
  petInfo: PetInfo;
  tutorInfo: TutorInfo;
  clinicId?: string;
  clinicName?: string;
  clinicAddress?: string;
  clinicPhone?: string;
  clinicCRMV?: string;
  vetName?: string;
  vetCRMV?: string;
  attendanceDate?: string;
  backgroundImage?: string; // Base64 da imagem de background
  backgroundImageContentType?: string; // MIME (ex.: image/png, image/jpeg)
}

// =============================================================================
// SERVIÇO DE GERAÇÃO DE DOCUMENTOS
// =============================================================================

/**
 * Gera um documento PDF veterinário em formato base64 com UUID e QR Code
 *
 * @param params - Parâmetros do documento a ser gerado
 * @returns Promise com objeto contendo base64 do PDF e UUID gerado
 */
export const generateVetDocument = async (
  params: GenerateDocumentParams,
): Promise<DocumentGenerationResult> => {
  try {
    // Gera um UUID único para o documento
    const documentUUID = uuid.v1();

    // Gera um idValue único baseado em timestamp + número aleatório
    // IMPORTANTE: Este valor deve ser o mesmo usado no PostDocumentReference
    const idValue =
      Date.now() +
      Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, "0");

    const qrCodeEncodedValue = base64.encode(
      JSON.stringify({
        organization: params?.clinicId?.split("-")?.[1] || "",
        patientId: params?.petInfo?.id,
        uuid: documentUUID,
        idValue,
      }),
    );

    const linkDocument = `${endpoints?.XDSB_domain}/portal/#/validateDocument/${qrCodeEncodedValue}`;

    // Gera o QR Code com o UUID (250x250 para boa resolução na página dedicada)
    const qrCodeBase64 = await generateQRCodeBase64(linkDocument, 300);

    // Adiciona o QR Code e UUID aos parâmetros do documento
    const paramsWithQRCode = {
      ...params,
      qrCodeBase64,
      documentUUID,
      linkDocument,
    };

    // Cria o componente React do documento
    const documentElement = createPdfElement(
      React.createElement(VetDocumentTemplate, paramsWithQRCode),
    );

    // Gera o PDF usando react-pdf/renderer
    const pdfBlob = await pdf(documentElement).toBlob();

    // Converte o Blob para base64
    const base64s = await blobToBase64(pdfBlob);

    return {
      base64: base64s,
      uuid: documentUUID,
      idValue,
    };
  } catch (error) {
    console.error("Erro ao gerar documento PDF:", error);
    throw new Error("Falha ao gerar documento. Por favor, tente novamente.");
  }
};

/**
 * Converte um Blob para string base64
 *
 * @param blob - Blob a ser convertido
 * @returns Promise com string base64
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove o prefixo "data:application/pdf;base64," se existir
      const base64 = result.split(",")[1] || result;
      resolve(base64);
    };

    reader.onerror = () => {
      reject(new Error("Erro ao converter PDF para base64"));
    };

    reader.readAsDataURL(blob);
  });
};

/**
 * Gera documento de tratamento individual
 */
export const generateTreatmentDocument = async (
  treatment: Treatment,
  petInfo: PetInfo,
  tutorInfo: TutorInfo,
  additionalInfo?: Partial<GenerateDocumentParams>,
): Promise<DocumentGenerationResult> => {
  return generateVetDocument({
    documentType: "treatment",
    treatments: [treatment],
    petInfo,
    tutorInfo,
    ...additionalInfo,
  });
};

/**
 * Gera documento de exame individual
 */
export const generateExamDocument = async (
  exam: Exam,
  petInfo: PetInfo,
  tutorInfo: TutorInfo,
  additionalInfo?: Partial<GenerateDocumentParams>,
): Promise<DocumentGenerationResult> => {
  return generateVetDocument({
    documentType: "exam",
    exams: [exam],
    petInfo,
    tutorInfo,
    ...additionalInfo,
  });
};

/**
 * Gera documento de relatório individual
 */
export const generateReportDocument = async (
  report: Report,
  petInfo: PetInfo,
  tutorInfo: TutorInfo,
  additionalInfo?: Partial<GenerateDocumentParams>,
): Promise<DocumentGenerationResult> => {
  return generateVetDocument({
    documentType: "report",
    reports: [report],
    petInfo,
    tutorInfo,
    ...additionalInfo,
  });
};

/**
 * Gera documento com toda uma seção (todos os tratamentos, por exemplo)
 */
export const generateSectionDocument = async (
  sectionType: "treatment" | "exam" | "report",
  items: Treatment[] | Exam[] | Report[],
  petInfo: PetInfo,
  tutorInfo: TutorInfo,
  additionalInfo?: Partial<GenerateDocumentParams>,
): Promise<DocumentGenerationResult> => {
  const params: GenerateDocumentParams = {
    documentType: sectionType,
    petInfo,
    tutorInfo,
    ...additionalInfo,
  };

  // Atribui os itens ao campo correto baseado no tipo
  if (sectionType === "treatment") {
    params.treatments = items as Treatment[];
  } else if (sectionType === "exam") {
    params.exams = items as Exam[];
  } else if (sectionType === "report") {
    params.reports = items as Report[];
  }

  return generateVetDocument(params);
};

/**
 * Faz download do documento PDF
 *
 * @param base64 - String base64 do PDF
 * @param filename - Nome do arquivo (sem extensão)
 */
export const downloadPDF = (base64: string, filename: string = "documento") => {
  // Cria um link temporário para download
  const linkSource = `data:application/pdf;base64,${base64}`;
  const downloadLink = document.createElement("a");
  downloadLink.href = linkSource;
  downloadLink.download = `${filename}_${Date.now()}.pdf`;
  downloadLink.click();
};

/**
 * Abre o PDF em uma nova aba
 *
 * @param base64 - String base64 do PDF
 */
export const openPDFInNewTab = (base64: string) => {
  const pdfBlob = base64ToBlob(base64, "application/pdf");
  const blobUrl = URL.createObjectURL(pdfBlob);
  window.open(blobUrl, "_blank");
};

/**
 * Converte base64 para Blob
 *
 * @param base64 - String base64
 * @param contentType - Tipo do conteúdo
 * @returns Blob
 */
const base64ToBlob = (base64: string, contentType: string = ""): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};
